import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { inviteVacantOwner } from "@/lib/invites.functions";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Mail } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/majitele-vacantu")({
  component: AdminVacantInvitesPage,
});

type InviteRow = {
  id: string;
  email: string;
  accepted_at: string | null;
  accepted_user_id: string | null;
  created_at: string;
  vakanty_to_grant: number;
  vakanty_purchased?: number;
};

type OwnerRow = {
  id: string;
  email: string | null;
  full_name: string | null;
  vakanty_purchased: number;
  created_at: string;
};

function AdminVacantInvitesPage() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [grants, setGrants] = useState<Record<string, string>>({});
  const [ownerCounts, setOwnerCounts] = useState<Record<string, string>>({});
  const invite = useServerFn(inviteVacantOwner);

  const { data: owners = [], isLoading: ownersLoading } = useQuery({
    queryKey: ["admin", "vakant-owners"],
    queryFn: async (): Promise<OwnerRow[]> => {
      const { data: roleRows, error: roleErr } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "vakant_owner");
      if (roleErr) throw roleErr;
      const ids = (roleRows ?? []).map((r: { user_id: string }) => r.user_id);
      if (!ids.length) return [];
      const { data: profs, error: profErr } = await supabase
        .from("profiles")
        .select("id,email,full_name,vakanty_purchased,created_at")
        .in("id", ids);
      if (profErr) throw profErr;
      return ((profs ?? []) as OwnerRow[]).sort((a, b) =>
        (a.full_name ?? a.email ?? "").localeCompare(b.full_name ?? b.email ?? ""),
      );
    },
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "vakant-invites"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vakant_invites")
        .select("id,email,accepted_at,accepted_user_id,created_at,vakanty_to_grant")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const invites = (data ?? []) as InviteRow[];
      const userIds = invites.map((i) => i.accepted_user_id).filter(Boolean) as string[];
      if (userIds.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id,vakanty_purchased")
          .in("id", userIds);
        const map = new Map((profs ?? []).map((p: { id: string; vakanty_purchased: number }) => [p.id, p.vakanty_purchased]));
        invites.forEach((i) => {
          if (i.accepted_user_id) i.vakanty_purchased = map.get(i.accepted_user_id) ?? 0;
        });
      }
      return invites;
    },
  });

  const updateOwnerCountMut = useMutation({
    mutationFn: async ({ userId, count }: { userId: string; count: number }) => {
      const { error } = await supabase.from("profiles").update({ vakanty_purchased: count }).eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Počet Vacantů uložen.");
      qc.invalidateQueries({ queryKey: ["admin", "vakant-owners"] });
      qc.invalidateQueries({ queryKey: ["admin", "vakant-invites"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateCountMut = useMutation({
    mutationFn: async ({ userId, count }: { userId: string; count: number }) => {
      const { error } = await supabase.from("profiles").update({ vakanty_purchased: count }).eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Počet Vacantů uložen.");
      qc.invalidateQueries({ queryKey: ["admin", "vakant-invites"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateGrantMut = useMutation({
    mutationFn: async ({ id, count }: { id: string; count: number }) => {
      const { error } = await supabase.from("vakant_invites").update({ vakanty_to_grant: count }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Počet Vacantů pro pozvánku uložen.");
      qc.invalidateQueries({ queryKey: ["admin", "vakant-invites"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const inviteMut = useMutation({
    mutationFn: async (em: string) => {
      const redirectTo = `${window.location.origin}/auth/reset-password`;
      await invite({ data: { email: em, redirectTo } });
    },
    onSuccess: () => {
      toast.success("Pozvánka odeslána.");
      setEmail("");
      qc.invalidateQueries({ queryKey: ["admin", "vakant-invites"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vakant_invites").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Pozvánka odstraněna.");
      qc.invalidateQueries({ queryKey: ["admin", "vakant-invites"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!v) return;
    inviteMut.mutate(v);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl">Majitelé Vacantů</h1>
        <p className="text-sm text-muted-foreground">
          Přidejte e-mail majitele. Pošleme mu pozvánku s odkazem pro nastavení hesla. Po
          potvrzení e-mailu získá roli „Majitel Vacantu" a přístup do své sekce.
        </p>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
            <div className="grow space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">E-mail</label>
              <Input
                type="email"
                required
                placeholder="majitel@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={inviteMut.isPending} className="gap-1.5">
              <Mail className="h-4 w-4" />
              {inviteMut.isPending ? "Odesílám…" : "Pozvat"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div>
        <h2 className="font-serif text-2xl">Majitelé Vacantů – seznam</h2>
        <p className="text-sm text-muted-foreground">
          Všichni uživatelé s rolí „Majitel Vacantu". Zde můžete nastavit počet vlastněných Vacantů.
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Uživatel</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Vacantů</TableHead>
                <TableHead className="text-right">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ownersLoading && (
                <TableRow>
                  <TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">
                    Načítám…
                  </TableCell>
                </TableRow>
              )}
              {!ownersLoading && owners.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">
                    Zatím žádní majitelé.
                  </TableCell>
                </TableRow>
              )}
              {owners.map((o) => {
                const current = ownerCounts[o.id] ?? String(o.vakanty_purchased ?? 0);
                return (
                  <TableRow key={o.id}>
                    <TableCell>{o.full_name ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{o.email ?? "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          className="h-8 w-20"
                          value={current}
                          onChange={(e) => setOwnerCounts((c) => ({ ...c, [o.id]: e.target.value }))}
                        />
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={updateOwnerCountMut.isPending}
                          onClick={() => {
                            const n = Math.max(0, parseInt(current || "0", 10) || 0);
                            updateOwnerCountMut.mutate({ userId: o.id, count: n });
                          }}
                        >
                          Uložit
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">—</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>



      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>E-mail</TableHead>
                <TableHead>Stav</TableHead>
                <TableHead>Vacantů</TableHead>
                <TableHead>Pozváno</TableHead>
                <TableHead className="text-right">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                    Načítám…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                    Zatím žádné pozvánky.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => {
                const uid = r.accepted_user_id;
                const current = uid ? (counts[uid] ?? String(r.vakanty_purchased ?? 0)) : "";
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.email}</TableCell>
                    <TableCell>
                      {r.accepted_at ? (
                        <Badge variant="default">Aktivní</Badge>
                      ) : (
                        <Badge variant="secondary">Čeká na přijetí</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {uid ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            className="h-8 w-20"
                            value={current}
                            onChange={(e) => setCounts((c) => ({ ...c, [uid]: e.target.value }))}
                          />
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={updateCountMut.isPending}
                            onClick={() => {
                              const n = Math.max(0, parseInt(current || "0", 10) || 0);
                              updateCountMut.mutate({ userId: uid, count: n });
                            }}
                          >
                            Uložit
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            className="h-8 w-20"
                            value={grants[r.id] ?? String(r.vakanty_to_grant ?? 0)}
                            onChange={(e) => setGrants((g) => ({ ...g, [r.id]: e.target.value }))}
                          />
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={updateGrantMut.isPending}
                            onClick={() => {
                              const raw = grants[r.id] ?? String(r.vakanty_to_grant ?? 0);
                              const n = Math.max(0, parseInt(raw || "0", 10) || 0);
                              updateGrantMut.mutate({ id: r.id, count: n });
                            }}
                          >
                            Uložit
                          </Button>
                          <span className="text-[10px] text-muted-foreground">přiřadí se po potvrzení e-mailu</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString("cs-CZ")}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteMut.mutate(r.id)}
                        disabled={deleteMut.isPending}
                        aria-label="Smazat"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
